import { ConfirmationDialogRenderer } from '@/components/playbook-editor/ConfirmationDialogRenderer'
import { UnifiedSettingsDialog } from '@/components/shared/UnifiedSettingsDialog'
import { ManageTeamsDialog } from '@/components/content-manager/ManageTeamsDialog'
import { NewFolderDialog } from '@/components/content-manager/NewFolderDialog'
import { NewPlaybookDialog } from '@/components/content-manager/NewPlaybookDialog'
import { NewDocumentDialog } from '@/components/content-manager/NewDocumentDialog'
import { AddToFolderDialog } from '@/components/content-manager/AddToFolderDialog'
import { RenameDialog } from '@/components/content-manager/RenameDialog'
import { PlaybookShareDialog } from '@/components/shared/PlaybookShareDialog'
import { TourController, WelcomeModal } from '@/components/onboarding'
import { parseSelectedItems, type ShareTarget, type RenameTarget } from './hooks/UseContentManagerState'
import type { UseContentManagerConfirmationsReturn } from '@/pages/content-manager/hooks'
import type { UseContentManagerActionsReturn } from '@/pages/content-manager/hooks'
import type { DocumentTemplateType, CreateDocumentSectionRequest } from '@/types/blocks'
import type { ContentType as DbContentType, PlaybookWithCount } from '@/db/Types'

/**
 * Modal manager interface (from useModalManager)
 */
interface ModalManager {
	isOpen: (name: string) => boolean
	close: (name: string) => void
}

/**
 * Props for the ContentManagerDialogs component
 */
interface ContentManagerDialogsProps {
	// Modal management
	modals: ModalManager

	// Data
	playbooks: PlaybookWithCount[]
	selectedItems: Set<string>

	// Share target
	shareTarget: ShareTarget | null
	onClearShareTarget: () => void

	// Rename target
	renameTarget: RenameTarget | null
	onClearRenameTarget: () => void
	onRename: (id: string | number, newName: string) => Promise<void>

	// Confirmations
	confirmations: UseContentManagerConfirmationsReturn

	// Actions
	actions: Pick<
		UseContentManagerActionsReturn,
		'handleCreatePlaybook' | 'handleCreateDocument' | 'handleAddToFolders'
	>

	// Onboarding
	showWelcome: boolean
	onStartTour: () => void
	onDismissWelcome: () => void
}

/**
 * ContentManagerDialogs - renders all modal dialogs for ContentManagerPage
 *
 * Follows the pattern established by PlaybookEditorDialogs to keep
 * dialog JSX separate from the main component logic.
 */
export function ContentManagerDialogs({
	modals,
	playbooks,
	selectedItems,
	shareTarget,
	onClearShareTarget,
	renameTarget,
	onClearRenameTarget,
	onRename,
	confirmations,
	actions,
	showWelcome,
	onStartTour,
	onDismissWelcome,
}: ContentManagerDialogsProps) {
	return (
		<div className="content-manager-modals">
			{/* New Playbook Dialog */}
			<NewPlaybookDialog
				isOpen={modals.isOpen('newPlaybook')}
				onClose={() => modals.close('newPlaybook')}
				onCreatePlaybook={actions.handleCreatePlaybook}
			/>

			{/* New Document Dialog */}
			<NewDocumentDialog
				isOpen={modals.isOpen('newDocument')}
				onClose={() => modals.close('newDocument')}
				onCreateDocument={(
					playbookId: string,
					name: string,
					templateType: DocumentTemplateType,
					sections: CreateDocumentSectionRequest[],
					description?: string
				) => actions.handleCreateDocument(playbookId, name, templateType, sections, description)}
				playbooks={playbooks}
			/>

			{/* Settings Dialog */}
			<UnifiedSettingsDialog
				isOpen={modals.isOpen('settings')}
				onClose={() => modals.close('settings')}
				context="content-manager"
			/>

			{/* Manage Teams Dialog */}
			<ManageTeamsDialog
				isOpen={modals.isOpen('manageTeams')}
				onClose={() => modals.close('manageTeams')}
			/>

			{/* New Folder Dialog */}
			<NewFolderDialog
				isOpen={modals.isOpen('newFolder')}
				onClose={() => modals.close('newFolder')}
			/>

			{/* Add to Folder Dialog */}
			<AddToFolderDialog
				isOpen={modals.isOpen('addToFolder')}
				onClose={() => modals.close('addToFolder')}
				selectedItems={parseSelectedItems(selectedItems).map(item => ({
					type: item.type as DbContentType,
					id: item.id,
				}))}
				onAddToFolders={actions.handleAddToFolders}
			/>

			{/* Share Playbook Dialog */}
			{shareTarget && (
				<PlaybookShareDialog
					isOpen={modals.isOpen('share')}
					onClose={() => {
						modals.close('share')
						onClearShareTarget()
					}}
					playbookId={shareTarget.id}
					playbookName={shareTarget.name}
					currentTeamId={shareTarget.teamId}
				/>
			)}

			{/* Rename Dialog */}
			<RenameDialog
				isOpen={modals.isOpen('rename')}
				target={renameTarget}
				onClose={() => {
					modals.close('rename')
					onClearRenameTarget()
				}}
				onRename={onRename}
			/>

			{/* Confirmation Dialogs */}
			<ConfirmationDialogRenderer confirmation={confirmations.deleteConfirm} />
			<ConfirmationDialogRenderer confirmation={confirmations.permanentDeleteConfirm} />
			<ConfirmationDialogRenderer confirmation={confirmations.restoreConfirm} />
			<ConfirmationDialogRenderer confirmation={confirmations.emptyTrashConfirm} />
			<ConfirmationDialogRenderer confirmation={confirmations.deleteFolderConfirm} />

			{/* Onboarding */}
			<TourController />
			<WelcomeModal
				isOpen={showWelcome}
				onStartTour={onStartTour}
				onDismiss={onDismissWelcome}
			/>
		</div>
	)
}
